import Vue from 'vue';
import { mapGetters } from 'vuex';
import moment from 'moment-timezone';
import _ from 'lodash';
import { cognitoMixins } from '@storm/mixins/cognito';
import Datepicker from 'vuejs-datepicker';
import Chart from 'chart.js';
import eventColors from './event-colors.json';

export default Vue.component('storm-analytics', {
  template: require('./template.html'),
  name: 'storm-analytics',
  mixins: [cognitoMixins],
  components: {
    Datepicker,
  },
  computed: {
    ...mapGetters(['userEvents', 'eventUser']),
    filteredEvents() {
      const events = this.userEvents;
      const sortOn = this.sortKey || this.cols[1];
      return _.chain(events)
        .thru((t) => {
          if (this.filterKey) {
            return _.reduce(
              t,
              (acc, row) => {
                // only gather the cols the table needs from data
                const e = [];
                _.forEach(this.cols, (c) => {
                  // clean up the values so that the
                  // searchable items are like how
                  // the template is written
                  const col = this.cleanItem(row[c], c, row);
                  e.push(col);
                });
                if (
                  _.filter(e, v =>
                    _(v)
                      .toLower()
                      .includes(this.filterKey.toLowerCase()),
                  ).length
                ) {
                  acc.push(row);
                }
                return acc;
              },
              [],
            );
          }
          return t;
        })
        .sort((a, b) => {
          // We'll sort accending by default and we'll also ignore case
          // since that may confuse non-technical users
          const ascDesc = this.sortMap[sortOn];
          a = _.isString(a[sortOn]) ? _.toLower(a[sortOn]) : a[sortOn]; // eslint-disable-line
          b = _.isString(b[sortOn]) ? _.toLower(b[sortOn]) : b[sortOn]; // eslint-disable-line
          return (a === b ? 0 : a < b ? -1 : 1) * ascDesc; // eslint-disable-line
        })
        .value();
    },
  },
  data() {
    const cols = ['type', 'time', 'browser'];
    const sortMap = _.zipObject(cols, _.fill(Array(cols.length), -1));
    return {
      cols,
      sortMap,
      sortKey: '',
      filterKey: '',
      paginate: ['eventlist'],
      loading: false,
      detailModal: false,
      activeEvent: {},
      charts: {
        line: {
          instance: null,
          labels: [],
          data: [],
          start: null, // weeks ago
          stop: null, // usually today
        },
        pie: {
          instance: null,
          labels: [],
          data: [],
          start: null, // weeks ago
          stop: null, // usually today
          colors: {
            default: 'rgba(69, 223, 195, .3)',
            ...eventColors,
          },
        },
      },
      daily: {},
      types: {},
    };
  },
  mounted() {
    this.initCharts();
  },
  methods: {
    buildData(params) {
      this.daily = {};
      this.types = {};
      let start;
      let stop;
      if (params && params.start && params.stop) {
        stop = this.charts.line.stop;
        start = this.charts.line.start;
      } else {
        stop = new Date().setHours(0, 0, 0, 0);
        this.charts.line.stop = new Date(stop);
        const now = new Date(new Date().setHours(0, 0, 0, 0));
        start = now.setDate(now.getDate() - 14);
        this.charts.line.start = new Date(start);
      }
      const range = this.getDateList(start, stop);
      _.forEach(range, (date) => {
        this.daily[date] = 0;
      });
      return new Promise((resolve) => {
        _.forEach(this.userEvents, (evt) => {
          // break out the daily totals
          const day = moment(evt.time).format('MM/DD/YY');
          if (typeof this.daily[day] !== 'undefined') {
            this.daily[day] += 1;
            if (this.types[evt.type]) {
              this.types[evt.type] += 1;
            } else {
              this.types[evt.type] = 1;
            }
          }
        });
        resolve();
      });
    },
    async initCharts(params) {
      await this.buildData(params);
      // area chart data
      const areaChart = this.$refs.eventsArea;
      this.charts.line.labels = [];
      this.charts.line.data = [];
      _.forOwn(this.daily, (total, day) => {
        this.charts.line.labels.push(day);
        this.charts.line.data.push(total);
      });
      const areaConfig = {
        type: 'line',
        data: {
          labels: this.charts.line.labels,
          datasets: [{
            borderColor: '#1ebda0',
            backgroundColor: 'rgba(69, 223, 195, .3)',
            label: 'Total Events',
            data: this.charts.line.data,
          }],
        },
        options: {
          maintainAspectRatio: false,
          title: {
            display: true,
            text: 'Recent Events',
          },
        },
      };
      if (this.charts.line.instance && params && params.update) {
        this.charts.line.instance.data.labels = this.charts.line.labels;
        this.charts.line.instance.data.datasets[0].data = this.charts.line.data;
        this.charts.line.instance.update(areaConfig);
      } else {
        this.charts.line.instance = new Chart(areaChart, areaConfig);
      }

      // pie chart data broken by event types
      const pieChart = this.$refs.eventsPie;
      this.charts.pie.labels = [];
      this.charts.pie.data = [];
      const colors = [];
      const chart = this.charts.pie;
      _.forOwn(this.types, (total, type) => {
        chart.labels.push(_.startCase(type));
        chart.data.push(total);
        colors.push(chart.colors[type] || chart.colors.default);
      });
      const pieConfig = {
        type: 'doughnut',
        data: {
          labels: this.charts.pie.labels,
          datasets: [{
            label: 'Type Totals',
            data: this.charts.pie.data,
            backgroundColor: colors,
          }],
        },
        options: {
          maintainAspectRatio: false,
          title: {
            display: true,
            text: 'Recent Event Types',
          },
        },
      };
      if (this.charts.pie.instance && params && params.update) {
        this.charts.pie.instance.data.labels = this.charts.pie.labels;
        this.charts.pie.instance.data.datasets[0].data = this.charts.pie.data;
        this.charts.pie.instance.update(areaConfig);
      } else {
        this.charts.pie.instance = new Chart(pieChart, pieConfig);
      }
    },
    viewEvent(evt) {
      this.activeEvent = evt;
      this.detailModal = true;
    },
    applyRange() {
      this.initCharts({
        update: true,
        start: this.charts.line.start,
        stop: this.charts.line.stop,
      });
    },
    formatLabel(date) {
      return moment(date).format('MM/DD/YY');
    },
    getDateList(startDate, stopDate) {
      const dateArray = [];
      let currentDate = moment(startDate);
      while (currentDate <= moment(stopDate)) {
        dateArray.push(moment(currentDate).format('MM/DD/YY'));
        currentDate = moment(currentDate).add(1, 'days');
      }
      return dateArray;
    },
    setSortKey(k) {
      this.sortKey = k;
      // Invert the value for ascDesc
      this.sortMap[k] = this.sortMap[k] * -1;
    },
    seperateCamalCase(str) {
      return str.replace(/([A-Z])/g, g => ` ${g[0].toLowerCase()}`);
    },
    formatDate(date) {
      return moment(date).format('MM/DD/YY');
    },
    formatTime(stamp) {
      return moment(stamp).format('MM/DD/YY hh:mm:ss A');
    },
    startCase(string) {
      return _.startCase(string);
    },
    cleanItem(item, col) {
      let i = item || '--';
      if (col === 'time') {
        i = this.formatTime(item);
      }
      if (col === 'type') {
        i = this.startCase(item);
      }
      return i;
    },
    highlight(ctx, query, col) {
      const c = this.cleanItem(ctx, col);
      return c.replace(
        new RegExp(query, 'ig'),
        match => `<span class='editorUi-searchHighlight'>${match}</span>`,
      );
    },
  },
});



// WEBPACK FOOTER //
// ./src/storm/pages/user-analytics/index.js